import React from "react";
import { useLanguage } from "../LanguageProvider";

// PDF Download Component with HoppHomes theme and better positioning
const PDFDownloadButton = ({ fileName, displayName, icon, position, roofType, label }) => {
  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = `/pdfs/${fileName}`;
    link.download = fileName;
    link.click();
  };

  return (
    <div className={`${position} flex flex-col items-center z-20`}>
      {/* Label above the circle */}
      <div className="text-white text-sm font-bold mb-3 text-center px-2">
        <div className="text-secondary text-xs font-extrabold tracking-wide mb-1">MŰSZAKI DOKUMENTÁCIÓ</div>
        <div className="text-white text-sm font-bold">{label}</div>
      </div>
      
      {/* Circle button */}
      <div style={{ width: '120px', height: '120px' }}>
        <button
          onClick={handleDownload}
          className="w-full h-full bg-gradient-to-br from-secondary via-hover to-primary rounded-full shadow-glow-turquoise hover:shadow-glow-turquoise transition-all duration-300 hover:scale-105 flex flex-col items-center justify-center text-primary font-bold group border-3 border-secondary hover:border-hover relative overflow-hidden"
          aria-label={`Letöltés: ${displayName}`}
        >
          {/* Background pattern */}
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
          
          {/* Content */}
          <div className="relative z-10 flex flex-col items-center">
            <div className="text-2xl mb-1 group-hover:scale-110 transition-transform duration-200">
              {icon}
            </div>
            <div className="text-center leading-tight px-1">
              <div className="text-[10px] font-extrabold tracking-wide">PDF</div>
              <div className="text-[9px] font-bold">LETÖLTÉS</div>
            </div>
          </div>

          {/* Hover effect overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-secondary/10 to-hover/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        </button>
      </div>
      
      {/* Tooltip */}
      <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 bg-black/90 text-white text-xs px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
        {roofType} műszaki leírás letöltése
      </div>
    </div>
  );
};

export default function PricingSection() {
  const { dict } = useLanguage();
  const pricing = dict.pricing || {};
  const houseTypes = pricing.houseTypes || [];

  return (
    <section className="py-20 bg-background relative overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 relative">
        <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-12 text-center tracking-tight">
          {pricing.sectionTitle}
        </h2>
        
        {/* PDF Download buttons above cards */}
        <div className="flex justify-center gap-16 mb-12">
          <PDFDownloadButton 
            fileName="lapostetos-mobilhaz-muszaki-dokumentacio.pdf"
            displayName="Lapostetős mobilház műszaki dokumentáció"
            icon="🏠"
            position="relative"
            roofType="Lapostetős"
            label="Lapostetős Mobilház"
          />
          <PDFDownloadButton 
            fileName="magastetos-mobilhaz-muszaki-dokumentacio.pdf"
            displayName="Magastetős mobilház műszaki dokumentáció"
            icon="🏘️"
            position="relative"
            roofType="Magastetős"
            label="Magastetős Mobilház"
          />
        </div>

        {/* Cards container */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {houseTypes.map((type, i) => (
            <div key={i} className="flex flex-col items-center bg-section rounded-modulive shadow-lg p-8 border border-secondary/30 hover:shadow-glow-turquoise transition-all duration-300 group">
              <div className="text-lg font-bold text-white mb-2 text-center">
                {type.name}
              </div>
              {type.note && (
                <div style={{ fontSize: '0.8em', color: '#b0b0b0', marginTop: '-0.5em', marginBottom: '0.5em', textAlign: 'center' }}>
                  {type.note}
                </div>
              )}
              <div className="text-secondary text-3xl font-extrabold mb-4">{type.price}</div>
              <ul className="mb-6 space-y-1">
                {type.features.map((f, j) => (
                  <li key={j} className="text-gray-300 text-base flex items-center gap-2">
                    <span className="inline-block w-2 h-2 bg-secondary rounded-full"></span> {f}
                  </li>
                ))}
              </ul>
              <button
                className="bg-secondary hover:bg-hover text-primary font-bold px-6 py-3 rounded-modulive text-lg shadow-glow-turquoise transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary/60 focus:ring-offset-2 hover:scale-105"
                tabIndex={0}
                aria-label={pricing.ariaLabel}
              >
                {type.cta}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
